import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { ReportParameter } from '../../shared/models/report-parameter.model';
import { ReportConstants } from '../../shared/constants/report.constants';
import { AutoCompleteDropdownComponent } from '../../shared/controls/auto-complete-dropdown/auto-complete-dropdown.component';
import { TabControlComponent } from '../../shared/controls/tab-control/tab-control.component';
import { TabPageComponent } from '../../shared/controls/tab-control/tab-page.component';

import { ErrorService } from '../../shared/services/error-service';
import { ToastService } from '../../shared/toast/toast.service';
import { DatePipe, formatDate } from '@angular/common';
import { ClientService, ClientAreaService, RenewalReportPackInput, ReportPackRenewalService } from '../../../service/proxy';

@Component({
    selector: 'renewal-report-selection',
    templateUrl: './renewal-report-selection.component.html',
    styleUrls: ['./renewal-report-selection.component.scss'],

    imports: [
        CommonModule,
        FormsModule,
        AutoCompleteDropdownComponent,
        TabControlComponent,
        TabPageComponent,
    ],

    providers: [
        ClientService,
        ReportPackRenewalService,
        ClientAreaService,
        DatePipe
    ]
})
export class RenewalReportSelectionComponent implements OnInit {

    private readonly REPORT_PACK_CODE = -1;
    public isSaving: boolean = false;

    public startDate!: string;
    public endDate!: string;
    public clients: { id: number; name: string; }[] = [];
    public clientAreas: { id: number; name: string; }[] = [];

    public reports = [
        { id: ReportConstants.PolicyScheduleReport, name: 'Policy Schedule Report' },
        { id: ReportConstants.PremiumsReport, name: 'Premiums Report' },
        { id: ReportConstants.ClaimsReport, name: 'Claims Report' },
        { id: ReportConstants.LimitsReport, name: 'Limits Report' },
        { id: ReportConstants.PerformanceBonusReport, name: 'Performance Report' },
        // { id: ReportConstants.InsuredDebtorsGrantedVsExposures, name: 'Insured Debtors Granted Vs Exposures' },
        { id: this.REPORT_PACK_CODE, name: 'Report Pack' },
    ];

    public endDateError = false;
    public expiryTypingTimer: any;

    public selectedReportId: number | undefined = undefined;
    public selectedClientId: number | undefined = undefined;
    public selectedClientAreaId: number | undefined = undefined;
    public formSubmitted: boolean = false;

    constructor(
        private router: Router,
        private errorService: ErrorService,
        private toastService: ToastService,
        private clientService: ClientService,
        private reportPackService: ReportPackRenewalService,
        private clientAreaService: ClientAreaService,
        private datePipe: DatePipe
    ) {}

    async ngOnInit(): Promise<void> {
        this.getClientsForLookup();

        // Handle whether the user clicked back from existing report selection
        await this.applyIncomingReportParams();
    }

    getClientsForLookup(): void {
        this.clientService.getAllClients()
            .subscribe({
                next: (response) => {
                    this.clients = response.results.map(client => ({
                        id: client.id,
                        name: client.clientName
                    }));
                },
                error: (error: any) => {
                    this.errorService.handleError(error);
                }
            }
        );
    }

    onClientSelectionChange(selectedId: number | null): void {
        this.selectedClientId = selectedId ?? undefined;
        this.selectedClientAreaId = undefined;
        this.clientAreas = [];

        if (this.selectedClientId) {
            this.getClientAreasForLookup();
        }
    }

    onClientAreaSelectionChange(selectedId: number | null): void {
        this.selectedClientAreaId = selectedId ?? undefined;
    }

    onReportSelectionChange(selectedId: number | null): void {
        this.selectedReportId = selectedId ?? undefined;
    }

    getClientAreasForLookup(): void {
        if (!this.selectedClientId) {
            return;
        }

        this.clientAreaService.getClientAreaLookup(this.selectedClientId)
            .subscribe({
                next: (clientAreas) => {
                    if (!clientAreas.length) {
                        this.toastService.show('No policies found for the selected client', 'info');
                        return;
                    }

                    this.clientAreas = clientAreas;
                },
                error: () => {
                    this.toastService.show('Failed to load client areas', 'error');
                }
            }
        );
    }

    private async applyIncomingReportParams(): Promise<void> {

        // If user navigated from report viewer with reportParams, prefill selection controls
        const incomingParams: any[] = (window.history.state && Array.isArray(window.history.state.reportParams))
            ? window.history.state.reportParams
            : [];

        if (!incomingParams || !Array.isArray(incomingParams) || incomingParams.length === 0) {
            return;
        }

        try {
            const startDateParam = incomingParams.find((p: any) => p.label === 'Start Date');

            if (startDateParam?.key) {
                const startDt = new Date(startDateParam.key);
                if (!isNaN(startDt.getTime())) {
                    this.startDate = startDt.toISOString().slice(0, 10); // "yyyy-MM-dd" for <input type="date">
                } 
                else {
                    this.startDate = String(startDateParam.key);
                }
            }

            const endDateParam = incomingParams.find((p: any) => p.label === 'End Date');

            if (endDateParam?.key) {
                const endDt = new Date(endDateParam.key);
                if (!isNaN(endDt.getTime())) {
                    this.endDate = endDt.toISOString().slice(0, 10); // "yyyy-MM-dd" for <input type="date">
                } 
                else {
                    this.endDate = String(endDateParam.key);
                }
            }

            const clientParam = incomingParams.find((p: any) => p.label === 'Client ID');
            if (clientParam?.key) {
                this.selectedClientId = Number(clientParam.key);
                
                // await client areas being loaded before using client area param
               if (this.selectedClientId) {
                    this.getClientAreasForLookup();
                }

                const clientAreaParam = incomingParams.find((p: any) => p.label === 'Client Area');
                if (clientAreaParam?.key) {
                    this.selectedClientAreaId = clientAreaParam.key;
                }
            }
        }
        catch (e) {
            console.warn('Failed to apply incomingParams reportParams', e);
        }
    }

    displayReport(option: any): string {
        return option?.name || 'Unnamed Report';
    }

    onRunReport(form: any): void {
        this.formSubmitted = true;

        if (!form.valid || !this.selectedClientAreaId || this.selectedReportId === undefined || !this.startDate || !this.endDate) {
            return;
        }

        const selectedClientArea = this.clientAreas.find(c => c.id === Number(this.selectedClientAreaId)) || { name: "Unknown Policy" };
        const clientAreaValue = selectedClientArea.name;

        const startDate = new Date(this.startDate);
        const endDate = new Date(this.endDate);

        const formatDate = (date: Date) => {
            const day = date.getDate().toString().padStart(2, '0');
            const month = date.toLocaleString('en-US', { month: 'short' });
            const year = date.getFullYear();

            return `${day} ${month} ${year}`;
        };

        const reportParams = [
            new ReportParameter(0, 'Start Date', startDate, formatDate(startDate)),
            new ReportParameter(1, 'End Date', endDate, formatDate(endDate)),
        ];

        const selectedClient = this.clients.find(c => c.id === Number(this.selectedClientId)) || { name: "Unknown Client" };
        const clientValue = selectedClient.name;

        reportParams.push(new ReportParameter(2, 'Client ID', this.selectedClientId, clientValue));
        reportParams.push(new ReportParameter(3, 'Client Area', this.selectedClientAreaId, clientAreaValue));

        if (this.selectedReportId === this.REPORT_PACK_CODE) {
            this.createReportPack(form);
        }
        else {
            this.router.navigate(['/report-viewer', this.selectedReportId], {
                state: {
                    reportParams
                }
            });
        }
    }

    createReportPack(form: any): void {
        if (!form.valid || this.isSaving) {
            return;
        }

        const input = new RenewalReportPackInput({
            clientId: this.selectedClientId || 0,
            clientAreaId: this.selectedClientAreaId || 0,
            startDate: new Date(this.startDate),
            endDate: new Date(this.endDate),
            createdAt: new Date(),
            emailAddress: undefined,
            reportStatus: undefined,
            dateSent: undefined,
            updatedAt: undefined,
            deletedAt: undefined
        });

        this.isSaving = true;

        this.reportPackService.createRenewalReportPack(input)
            .subscribe({
                next: () => {
                    this.toastService.show('Renewal Report Pack requested. Email will follow.', 'success');
                },
                error: (err: any) => this.errorService.handleError(err),
                complete: () => this.isSaving = false
            }
        );
    }

    onEndDateInput(): void {
        clearTimeout(this.expiryTypingTimer);
            this.expiryTypingTimer = setTimeout(() => {
                this.validateEndDate();
            },
        );
    }

    validateEndDate(): void {
        const startDateStr = this.startDate;
        const endDateStr = this.endDate;

        const fullDateRegex = /^\d{4}-\d{2}-\d{2}$/;

        if (fullDateRegex.test(startDateStr) && fullDateRegex.test(endDateStr)) {
            const expiryYear = new Date(this.endDate).getFullYear();

            // Ignore premature values like year 0002
            if (expiryYear < 1000) {
                this.endDateError = false;
                return;
            }

            if (this.endDate < this.startDate) {
                this.endDateError = true;
            }
            else {
                this.endDateError = false;
            }
        }
    }
}